import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './AppIcon';
import useSubscriptionReadOnly from '../hooks/useSubscriptionReadOnly';
import { useAuth } from '../contexts/AuthContext';

/**
 * Banner shown app-wide when the company's Stripe subscription is in any
 * read-only state (past_due, unpaid, canceled, paused). The app still
 * renders so the user can see their data; create / edit affordances are
 * gated separately via useSubscriptionReadOnly.
 *
 * Copy and CTA branch on (status, role). Admins get an actionable CTA
 * that links to /subscription-management. Non-admins get a "contact your
 * admin" variant with no CTA — they can't fix billing themselves and an
 * unactionable button is worse than no button.
 */

const PAYMENT_OVERDUE = ['past_due', 'unpaid'];

// Status → audience-aware copy bundles. Each cell is { title, body, ctaLabel? }.
// ctaLabel omitted = no CTA rendered (non-admin variants).
const BANNER_COPY = {
  past_due: {
    admin: {
      title: 'Your payment is overdue.',
      body: "You can view your data but can't create or edit anything until your payment is updated.",
      ctaLabel: 'Update Payment →',
    },
    nonAdmin: {
      title: "Your company's account has a billing issue.",
      body: 'Some actions are temporarily disabled. Contact your admin to update payment.',
    },
  },
  unpaid: {
    admin: {
      title: 'Your payment is overdue.',
      body: "You can view your data but can't create or edit anything until your payment is updated.",
      ctaLabel: 'Update Payment →',
    },
    nonAdmin: {
      title: "Your company's account has a billing issue.",
      body: 'Some actions are temporarily disabled. Contact your admin to update payment.',
    },
  },
  canceled: {
    admin: {
      title: 'Your subscription is cancelled.',
      body: 'Your data is preserved and read-only. Reactivate to resume full access.',
      ctaLabel: 'Reactivate →',
    },
    nonAdmin: {
      title: "Your company's subscription is cancelled.",
      body: 'Some actions are disabled. Contact your admin to reactivate.',
    },
  },
  paused: {
    admin: {
      title: 'Your trial has ended.',
      body: 'Add a payment method to continue using SiteProof.',
      ctaLabel: 'Add Payment →',
    },
    nonAdmin: {
      title: "Your company's trial has ended.",
      body: 'Some actions are disabled. Contact your admin to add a payment method.',
    },
  },
};

const SubscriptionReadOnlyBanner = ({ className = '' }) => {
  const navigate = useNavigate();
  const { isReadOnly, status } = useSubscriptionReadOnly();
  const { userProfile } = useAuth();

  if (!isReadOnly) return null;

  const isAdmin = userProfile?.role === 'admin';
  const copyBundle = BANNER_COPY?.[status];
  if (!copyBundle) return null;

  const copy = isAdmin ? copyBundle.admin : copyBundle.nonAdmin;

  return (
    <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl ${className}`}>
      <div className="flex items-start gap-3">
        <Icon name="Lock" size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            {copy.title}
          </p>
          <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
            {copy.body}
          </p>
        </div>
      </div>
      {copy.ctaLabel && (
        <button
          onClick={() => navigate('/subscription-management')}
          className="text-sm font-semibold px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap flex-shrink-0 transition-colors"
        >
          {copy.ctaLabel}
        </button>
      )}
    </div>
  );
};

// Re-export for tests / future PaymentOverdue special-cases.
export { PAYMENT_OVERDUE, BANNER_COPY };

export default SubscriptionReadOnlyBanner;
